import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthProvider";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useInterval } from "../../utils/useInterval";
import { isCheckActive, sortCasesNewestFirst } from "./caseFilters";

const POLL_DELAYS = [3000, 5000, 10000, 15000];

// Loads the user's Αστικά cases and keeps them fresh:
// - refetches when the header reports unseen notifications (localStorage "notif")
// - polls with backoff while any case check is queued/processing/retrying
// - mirrors the list into localStorage "cases" (read by Updates.jsx)
export default function useCases() {
  const [allCases, setAllCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState(true);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const { checking } = useContext(AuthContext);

  useInterval(async () => {
    setNotif(JSON.parse(localStorage.getItem("notif") === "0" ? false : true));
  }, 2000);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getCases = async () => {
      try {
        const response = await axiosPrivate.get("/cases", {
          signal: controller.signal,
        });
        if (!isMounted) return;
        setAllCases(sortCasesNewestFirst(response.data));
        setLoading(false);
        setNotif(false);
        localStorage.setItem("notif", "0");
      } catch (err) {
        console.error(err);
        if (!axios.isCancel(err)) {
          navigate("/login", { state: { from: location }, replace: true });
        }
      }
    };

    notif && getCases();

    return () => {
      isMounted = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notif]);

  useEffect(() => {
    localStorage.setItem("cases", JSON.stringify(allCases));
  }, [allCases]);

  const hasActiveCheck = useMemo(
    () =>
      allCases.some(isCheckActive) ||
      (Array.isArray(checking) && checking.length > 0),
    [allCases, checking]
  );

  useEffect(() => {
    if (!hasActiveCheck) return undefined;

    let cancelled = false;
    let timer;
    let attempt = 0;

    const schedule = () => {
      if (cancelled || document.hidden) return;
      const delay = POLL_DELAYS[Math.min(attempt, POLL_DELAYS.length - 1)];
      timer = window.setTimeout(poll, delay);
    };

    const poll = async () => {
      if (cancelled || document.hidden) return;
      try {
        const response = await axiosPrivate.get("/cases", { timeout: 8000 });
        if (cancelled) return;
        const nextCases = sortCasesNewestFirst(response.data);
        setAllCases(nextCases);
        if (!nextCases.some(isCheckActive)) return;
      } catch {}
      attempt += 1;
      schedule();
    };

    const handleVisibilityChange = () => {
      window.clearTimeout(timer);
      if (!document.hidden) schedule();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    schedule();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearTimeout(timer);
    };
  }, [axiosPrivate, hasActiveCheck]);

  const addCase = useCallback((singleCase) => {
    setAllCases((prevCases) => [singleCase, ...prevCases]);
  }, []);

  // Accepts a number or string id (CaseCard passes the id as a string).
  const removeCase = useCallback((caseId) => {
    const target = String(caseId);
    setAllCases((prevCases) =>
      prevCases.filter((caseItem) => String(caseItem.id) !== target)
    );
  }, []);

  // patch: object merged into the case, or (case) => partial object.
  const updateCase = useCallback((caseId, patch) => {
    const target = String(caseId);
    setAllCases((prevCases) =>
      prevCases.map((caseItem) =>
        String(caseItem.id) === target
          ? {
              ...caseItem,
              ...(typeof patch === "function" ? patch(caseItem) : patch),
            }
          : caseItem
      )
    );
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await axiosPrivate.get("/cases");
      setAllCases(sortCasesNewestFirst(response.data));
    } catch (err) {
      console.error(err);
    }
  }, [axiosPrivate]);

  return { allCases, loading, addCase, removeCase, updateCase, refresh };
}
